/**
 * PORTED — RO-3-04 / #44297.
 * Origin: `excellent/app/_modes/portfolio-board.tsx` + portfolio-board-client + rail.
 *
 * Changes:
 *   server listPipelines/listOpportunities → useApi(customers.board)
 *   next/navigation router.refresh → mutate the board op
 *   PipelineRail + PortfolioBoardClient co-located here
 * Live Next originals UNTOUCHED (D6).
 */

import { EmptyState, useOpenObject, usePageRail, type RailOption } from "@excellent/ui";
import { Kanban } from "@excellent/ui/icons";
import { apiCall, useApi } from "@excellent/ui/api";
import { customers } from "@excellent/ui/api/contracts/customers";
import { opportunities as oppsContract } from "@excellent/ui/api/contracts/crm";
import {
  PipelineSurface,
  type PipelineSurfaceStage,
  type PipelineSurfaceDeal,
} from "@excellent/ui/blocks/crm/pipeline-surface";

function PipelineRail({
  pipelines,
  selectedSlug,
}: {
  pipelines: { slug: string; name: string }[];
  selectedSlug: string;
}) {
  const options: RailOption[] = pipelines.map((p) => ({
    value: p.slug,
    label: p.name,
    href: `/customers?view=board&pipeline=${encodeURIComponent(p.slug)}`,
  }));
  usePageRail({
    groupLabel: "Board",
    representation: "segmented",
    spec: { label: "Pipeline", value: selectedSlug, options },
  });
  return null;
}

function PortfolioBoardClient({
  title,
  stages,
  deals,
  onMoved,
}: {
  title: string;
  stages: PipelineSurfaceStage[];
  deals: PipelineSurfaceDeal[];
  onMoved: () => void;
}) {
  const openObject = useOpenObject();
  return (
    <PipelineSurface
      title={title}
      stages={stages}
      deals={deals}
      onMove={async (dealId, stageId) => {
        await apiCall(oppsContract.move_stage, {
          params: { id: dealId },
          body: { stage_id: stageId },
        });
        onMoved();
      }}
      onOpen={(uid) => openObject(uid)}
    />
  );
}

export function PortfolioBoard({ pipelineSlug = null }: { pipelineSlug?: string | null }) {
  const { data, error, isLoading, mutate } = useApi(customers.board, {
    query: pipelineSlug ? { pipeline: pipelineSlug } : {},
  });

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading pipeline…</p>;
  }
  if (error) {
    return <p className="type-body text-destructive">{error.message ?? String(error)}</p>;
  }
  if (!data || data.empty || !data.stages || !data.deals || !data.title) {
    return (
      <EmptyState
        variant="inline"
        icon={<Kanban className="size-5" />}
        title="No pipeline configured"
        description="Set up a pipeline to work your deals as a board."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {data.pipelines.length > 1 && data.selectedSlug ? (
        <PipelineRail pipelines={data.pipelines} selectedSlug={data.selectedSlug} />
      ) : null}
      <PortfolioBoardClient
        title={data.title}
        stages={data.stages as PipelineSurfaceStage[]}
        deals={data.deals as PipelineSurfaceDeal[]}
        onMoved={() => void mutate()}
      />
    </div>
  );
}
